import { formatCurrency } from "@theme/utilities";

/**
 * Bundle price display.
 *
 * For bundle products (Shopify fixed bundles), shows the combined original price of the
 * bundled items with a strikethrough beside the bundle price, e.g. ~~$50~~ $40.
 *
 * Bundle components are read from the Storefront API (tokenless access), so the combined
 * price always reflects the current price and quantity of every item in the bundle.
 *
 * Price containers opt in with `data-bundle-variant-id` (see snippets/price.liquid).
 * Non-bundle variants resolve to null and are left untouched.
 */

const API_URL = "/api/2026-07/graphql.json";
const BATCH_SIZE = 5;
const BATCH_DELAY = 50;

const QUERY = `
  query BundleComponents($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        requiresComponents
        price { amount }
        components(first: 25) {
          nodes {
            quantity
            productVariant { price { amount } }
          }
        }
      }
    }
  }
`;

/** @type {Map<string, Promise<{price: number, compareAtPrice: number} | null>>} */
const cache = new Map();
/** @type {Map<string, (value: {price: number, compareAtPrice: number} | null) => void>} */
const pending = new Map();
let flushTimer = null;

const toCents = (amount) => Math.round(parseFloat(amount) * 100);

/**
 * Get the bundle price and combined price of its items for a variant (batched + cached).
 * @param {string|number} variantId
 */
function getBundlePrices(variantId) {
  const id = String(variantId);

  if (!cache.has(id)) {
    cache.set(id, new Promise((resolve) => pending.set(id, resolve)));
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, BATCH_DELAY);
  }

  return cache.get(id);
}

function flush() {
  const entries = [...pending.entries()];
  pending.clear();

  for (let i = 0; i < entries.length; i += BATCH_SIZE) {
    const batch = entries.slice(i, i + BATCH_SIZE);

    fetchBatch(batch.map(([id]) => id))
      .then((results) => batch.forEach(([id, resolve]) => resolve(results[id] ?? null)))
      .catch(() => {
        // Retry one by one so a single failing variant doesn't hide the others
        batch.forEach(([id, resolve]) => {
          fetchBatch([id])
            .then((results) => resolve(results[id] ?? null))
            .catch((error) => {
              console.warn(`Bundle price: could not load bundle components for variant ${id}`, error);
              resolve(null);
            });
        });
      });
  }
}

/**
 * @param {string[]} ids - Variant IDs
 * @returns {Promise<Record<string, {price: number, compareAtPrice: number}>>}
 */
async function fetchBatch(ids) {
  const response = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      query: QUERY,
      variables: {
        ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
        country: window.Shopify?.country || null,
      },
    }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const { data, errors } = await response.json();
  if (errors?.length) throw new Error(errors[0].message);

  /** @type {Record<string, {price: number, compareAtPrice: number}>} */
  const results = {};

  for (const node of data?.nodes || []) {
    const components = node?.components?.nodes || [];
    if (!node?.requiresComponents || components.length === 0) continue;

    const compareAtPrice = components.reduce(
      (total, component) => total + toCents(component.productVariant.price.amount) * component.quantity,
      0
    );

    results[node.id.split("/").pop()] = { price: toCents(node.price.amount), compareAtPrice };
  }

  return results;
}

/**
 * Show the combined price of the bundled items (strikethrough) in a price container.
 * @param {HTMLElement} container - The `.price-container` element
 * @param {{variantId?: string|number, quantity?: number}} [options]
 */
async function apply(container, { variantId, quantity = 1 } = {}) {
  if (!container) return;
  if (variantId) container.dataset.bundleVariantId = String(variantId);

  const id = container.dataset.bundleVariantId;
  if (!id || container.dataset.quantityBreaks === "true") return;

  const result = await getBundlePrices(id);

  // Skip if the variant changed while loading, or if this is not a discounted bundle
  if (!result || container.dataset.bundleVariantId !== id) return;
  if (result.compareAtPrice <= result.price) return;

  const salePriceDiv = container.querySelector(".price__sale");
  if (!salePriceDiv) return;

  const moneyFormat = container.dataset.moneyFormat || window.FoxTheme?.moneyFormat || "${{amount}}";
  const compareText = formatCurrency(result.compareAtPrice * quantity, moneyFormat);

  let comparePriceEl = salePriceDiv.querySelector(".compare-at-price");
  if (!comparePriceEl) {
    comparePriceEl = document.createElement("span");
    comparePriceEl.className = "compare-at-price";
    salePriceDiv.appendChild(comparePriceEl);
  }

  if (comparePriceEl.textContent !== compareText) comparePriceEl.textContent = compareText;
  container.classList.remove("price--no-compare");
  container.classList.add("price--on-sale");
}

function scan(root) {
  if (!(root instanceof Element)) return;
  if (root.matches("[data-bundle-variant-id]")) apply(root);
  root.querySelectorAll("[data-bundle-variant-id]").forEach((container) => apply(container));
}

window.FoxTheme = window.FoxTheme || {};
window.FoxTheme.bundlePrice = { apply };

scan(document.documentElement);

// Price containers are re-rendered on variant change, quick add, filtering, etc.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => scan(node));
  }
}).observe(document.documentElement, { childList: true, subtree: true });
